import { Component, OnInit } from '@angular/core';
import { AlertMessageService } from '../../services/alert-message.service';
import { AlertifyService } from '../../services/alertify.service';
import { AuthService } from '../../services/auth.service';
import { Note } from '../../models/note';
import { ProjectDataService } from '../../services/project-data-service';
import { UserService } from '../../services/user.service';
import { NotesService } from '../../services/note.service';
import { Router } from '@angular/router';

@Component({
    selector: 'or-notifications',
    templateUrl: './notifications.component.html',
    styleUrls: ['./notifications.component.scss'],
    standalone: false
})
export class NotificationsComponent implements OnInit {

  items: any;
  loading = false;
  isClientview: boolean = false;
  vendorHasNewNotes: boolean = false;
  vendorNotes: Note[] = [];
  clientHasNewNotes: boolean = false;
  clientNotes: Note[] = [];

  constructor(private alertService: AlertMessageService,
    private projectDataService: ProjectDataService,
    private alertify: AlertifyService,
    public auth: AuthService,
    private router: Router,
    private notesService: NotesService,
    public userService: UserService) {   }

  ngOnInit(): void {

    setTimeout(() => this.checkNewMessages(), 5000);

    // Subscribe to the event newMessageReceived from AlertService.
    this.alertService.newMessageReceived.subscribe(data => {
      if (data.action == 'notify') {
        this.blinkNotifications();
      }
    });

    // Subscribe to open new notifications (bell icon)
    this.alertService.newMessageReceived.subscribe(data => {
      if (data.action == 'blink' && data.data === this.auth.getUser().id) {
        this.blinkNotifications();
      }
    });

    // Initial fetch for vendors
    this.fetchVendorNotes();

    // Initial fetch for clients
    this.fetchClientNotes();
  }

  checkNewMessages() {
    let user = this.auth.getUser();
    this.alertService.checkNewMessages(user.instanceId, user.lastAlertViewDate).subscribe(data => {
      if (data) {
        this.blinkNotifications();
      }
    }, error => {
        console.log('Unable to check notifications');
    });
  }

  blinkNotifications() {
    let user = this.auth.getUser();
    user.hasNewMessages = true;
    this.auth.setUser(user);
  }

  openNotesSession(note: any) {
    if (note.bidId) {
      this.router.navigate(['/bids', note.bidId]);
      return;
    }
    this.router.navigate(['/projects', note.projectId], {
      queryParams: { openNotes: 'true' }
    });
  }

  private readonly emptyGuid = '00000000-0000-0000-0000-000000000000';

  // Resolve the bid id for a bid-note alert: prefer the structured bidId (once the alert pipeline
  // carries it), otherwise parse it from the /bids/{id} link embedded in the alert message HTML.
  private resolveBidId(message: any): string | null {
    if (message?.bidId && message.bidId !== this.emptyGuid) return message.bidId;
    const match = /\/bids\/([0-9a-fA-F-]{36})/.exec(message?.message ?? '');
    return match ? match[1] : null;
  }

  // A note alert that belongs to a bid rather than a project. Used to show the bid icon and deep-link.
  isBidAlert(message: any): boolean {
    return message?.type === 'NoteAdded'
      && (!!this.resolveBidId(message) || !message.projectId || message.projectId === this.emptyGuid);
  }

  navigateFromAlert(message: any) {
    const emptyGuid = this.emptyGuid;
    // Bid-note alerts deep-link to the bid (id from the structured field or the message link).
    const bidId = this.resolveBidId(message);
    if (bidId) {
      this.router.navigate(['/bids', bidId]);
      return;
    }
    // No real projectId and no bid — don't redirect to a broken URL.
    if (!message.projectId || message.projectId === emptyGuid) {
      return;
    }
    if (message.type === 'NoteAdded') {
      this.router.navigate(['/projects', message.projectId], {
        queryParams: { openNotes: 'true' }
      });
    } else {
      this.router.navigate(['/projects', message.projectId]);
    }
  }

  openMessages() {
    this.loading = true;
    let user = this.auth.getUser();
    let lastViewTimestamp = new Date(user.lastAlertViewDate).getTime();

    this.alertService.getLatestAlerts().subscribe(data => {
        this.items = data.filter(item => {
          var date = new Date(item.lastUpdated);
          date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
          item.time = this.timeSince(date);
          item.isNew = (new Date(item.lastUpdated).getTime() > lastViewTimestamp) || (item.time == 'just now');
          return item;
        });
      },
      error => {
        this.alertify.error('Unable to get notifications');
      },
      () => {
        // Set the LastAlertViewDate and hasNewMessages = false when Open the Messages
        user.hasNewMessages = false;
        var date = new Date();
        user.lastAlertViewDate = new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), date.getUTCHours(), date.getUTCMinutes(), date.getUTCSeconds());
        this.auth.setUser(user);
        this.loading = false;
      });
  }

  timeSince(date) {
    const intervals = [
      { label: 'year', seconds: 31536000 },
      { label: 'month', seconds: 2592000 },
      { label: 'day', seconds: 86400 },
      { label: 'hour', seconds: 3600 },
      { label: 'minute', seconds: 60 },
      { label: 'second', seconds: 0 }
    ];

    const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
    const interval = intervals.find(i => i.seconds < seconds);
    const count = Math.floor(seconds / interval.seconds);
    if (!isFinite(count) && interval.label == 'second')
        return 'just now';

    return `${count} ${interval.label}${count !== 1 ? 's' : ''} ago`;
  }

  fetchClientNotes() {
    if (this.auth.isClient()) {
      let user = this.auth.getUser();
      const lastUserLogin = new Date(user.lastLogin).getTime();
      this.notesService.getClientNotes(user.clientId).subscribe(data => {
        this.clientNotes = data.filter(note => {
          const date = new Date(note.cDate);
          date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
          note.time = this.timeSince(date);
          return new Date(note.cDate).getTime() > lastUserLogin;
        });
        this.clientHasNewNotes = this.clientNotes.length > 0;
      }, error => {
        console.log('Unable to fetch client notes');
      });
    }
  }

  openClientMessages() {
    let user = this.auth.getUser();
    this.clientHasNewNotes = false;
    user.lastLogin = new Date();
    this.auth.setUser(user);
  }

  fetchVendorNotes() {
    if (this.auth.isVendor()) {
      let user = this.auth.getUser();
      const lastUserLogin = new Date(user.lastLogin).getTime();
      this.notesService.getVendorNotes(user.vendorId).subscribe(data => {
        this.vendorNotes = data.filter(note => {
          var date = new Date(note.cDate);
          date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
          note.time = this.timeSince(date);
          return new Date(note.cDate).getTime() > lastUserLogin;
        });
        this.vendorHasNewNotes = this.vendorNotes.length > 0;
      }, error => {
        this.alertify.error('Unable to fetch vendor notes');
      });
    }
  }

  openVendorMessages() {
    let user = this.auth.getUser();
    this.vendorHasNewNotes = false;
    user.lastLogin = new Date();
    this.auth.setUser(user);
  }
}
